import React, { FC, ReactElement } from 'react';

import dayjs from 'dayjs';
import {
  AngleRightIcon,
  Box,
  Flex,
  IconSize,
  OHeaderFive,
  OSmallParagraph,
  Tooltip,
} from '@lifechurch/react-ion';
import styled from 'styled-components';

import Router from 'next/router';
import { useTranslation } from 'react-i18next';

const StyledFlex = styled(Flex)`
  button {
    margin-inline-start: 2px;
    margin-top: 1px;
    align-self: baseline;
  }
  h2 {
    margin-inline-start: auto;
  }
`;

const StyledTableBody = styled(Box)`
  background-color: white;
  border-radius: 2px;

  & > :nth-child(odd) {
    background-color: ${props => props.theme.colors.gray5};
  }
`;

const ClickableTableRow = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding-top: 3px;
  padding-inline-end: 0;
  padding-inline-start: 8px;
  align-items: center;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1rem;
  position: relative;
  cursor: pointer;

  p {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  div&:hover {
    background-color: ${props => props.theme.colors.action.light};
    color: ${props => props.theme.colors.action.default};

    svg path {
      fill: ${props => props.theme.colors.action.default};
    }

    &:after {
      display: none;
    }

    &:not(:first-child) {
      box-shadow: 0px -1px ${props => props.theme.colors.action.light};
    }
  }
`;

interface TableRowProps {
  date: string;
  attenders: string;
  onClick?: null | (() => void);
}

interface TableProps {
  serviceData: TopService[] | undefined;
}

export interface TopService {
  uniqueAttenders: number;
  serviceDate: string;
  serviceId: string;
}

const HeaderItem: React.FC = ({ children }) => (
  <Box display='flex' fontWeight={2}>
    {children}
  </Box>
);

const TableRow: React.FC<TableRowProps> = ({ date, attenders, onClick }) => {
  return (
    <ClickableTableRow data-testid='serviceInfo-tableRow' onClick={() => onClick && onClick()}>
      <StyledFlex>
        <OSmallParagraph fontWeight={600} marginInlineEnd={2}>
          {dayjs(date).format('h:mm A')}
        </OSmallParagraph>
        <OSmallParagraph>{dayjs(date).format('ddd, MMM D')}</OSmallParagraph>
      </StyledFlex>
      <StyledFlex>
        <OSmallParagraph>{attenders}</OSmallParagraph>
        <OSmallParagraph mx={3}>
          <AngleRightIcon size={IconSize.SM} />
        </OSmallParagraph>
      </StyledFlex>
    </ClickableTableRow>
  );
};

const TableBody: React.FC = ({ children }) => {
  return <StyledTableBody>{children}</StyledTableBody>;
};

const AnalyticsOverviewTopServiceBox: FC<TableProps> = ({ serviceData }): ReactElement => {
  const { t } = useTranslation(['analytics_overview', 'common']);

  return (
    <StyledFlex flexDirection='column'>
      <StyledFlex>
        <OHeaderFive marginInlineEnd={1}>{t('top_attended_services')}</OHeaderFive>
        <Tooltip
          content={'Services with the highest number of attenders during the selected timeframe.'}
          placement='top'
        />
      </StyledFlex>
      <Flex
        marginBlockStart={5}
        marginBlockEnd={3}
        marginInlineStart={2}
        marginInlineEnd={7}
        fontSize={2}
        justifyContent='space-between'
      >
        <HeaderItem>{t('common:service')}</HeaderItem>
        <HeaderItem>{t('unique_attenders')}</HeaderItem>
      </Flex>
      <TableBody>
        {serviceData?.map(({ serviceDate, uniqueAttenders, serviceId }) => (
          <TableRow
            key={'service-' + serviceId}
            date={serviceDate}
            attenders={uniqueAttenders.toLocaleString()}
            onClick={() =>
              Router.push('/analytics/services/[id]', `/analytics/services/${serviceId}`)
            }
          />
        ))}
      </TableBody>
    </StyledFlex>
  );
};

export default AnalyticsOverviewTopServiceBox;
